import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DropdownButton,
  InputGroup,
  MenuItem,
  Modal,
} from 'react-bootstrap';
import type { FormEvent, VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers';
import { getLicense } from '../../../api/licenses';
import { useOnMount } from '../../../hooks';
import useListLicenses from '../../../hooks/api/licenses/useListLicenses';
import { TextArea, TextInput } from '../../generic';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import LicenseOptions from '../../generic/LicenseOptions';
import Loader from '../../generic/Loader';
import Select from '../../generic/Select/Select';
import { licensingRuleTypeToActionRevamp } from '../../shared/policies/utils';
import type { LicensingRuleType } from '../../../../shared/model-typings';
import type { LicensingRule } from '../../../../typings/api';
import type { License, RuleDepthCondition, RuleLinkingCondition, SelectOption } from '../../../types';

import LicenseDetail from './LicenseDetail';

import styles from './RuleModal.module.less';

export type NewRule = Omit<LicensingRule, 'ruleId' | 'createdAt' | 'updatedAt'>;
export type RuleModalMode =
  | {
      editType: 'CREATE';
      type: LicensingRuleType;
      rules: LicensingRule[];
    }
  | {
      editType: 'EDIT';
      rules: LicensingRule[];
    };

type RuleModalProps = {
  show: boolean;
  mode: RuleModalMode;
  onClose(): void;
  onSubmit(newRules: Array<{ rule: NewRule; editedRuleId?: number }>): void;
};

type DepthConditionState = {
  enabled: boolean;
  value: RuleDepthCondition;
  option?: SelectOption<RuleDepthCondition>;
};

type NameConditionState = {
  enabled: boolean;
  value: string;
};

type LinkingConditionState = {
  enabled: boolean;
  value: RuleLinkingCondition;
  option?: SelectOption<RuleLinkingCondition>;
};

type RuleModalState = {
  type: LicensingRuleType;
  licenses?: License[];
  notes: string;
  loading: boolean;
  error: string | null;
  conditions: {
    depth: DepthConditionState;
    name: NameConditionState;
    linking: LinkingConditionState;
  };
};

const linkSelectOptions: Array<SelectOption<RuleLinkingCondition>> = [
  {
    value: 'STATIC',
    label: 'Statically',
  },
  {
    value: 'DYNAMIC',
    label: 'Dynamically',
  },
];

const depthSelectOptions: Array<SelectOption<RuleDepthCondition>> = [
  {
    value: 'DIRECT',
    label: 'In your code',
  },
  {
    value: 'DEEP',
    label: 'In a dependency',
  },
];

function makeInitialState(mode: RuleModalMode): RuleModalState {
  // NOTE: Ensure all state properties are returned. The result of this
  // function is passed to setState when opening the modal to ensure it's in a
  // clean state. If a property is missing, it won't be cleared from a previous
  // usage of the modal—setState merges its input with the current component's
  // state.
  if (mode.editType === 'CREATE') {
    return {
      type: mode.type,
      licenses: [],
      notes: '',
      loading: false,
      error: null,
      conditions: {
        depth: {
          enabled: false,
          value: 'DIRECT',
          option: undefined,
        },
        name: {
          enabled: false,
          value: '',
        },
        linking: {
          enabled: false,
          value: 'STATIC',
          option: undefined,
        },
      },
    };
  }

  return {
    type: mode.rules[0]?.type || 'denied_license',
    licenses: _.compact(mode.rules.map((r) => r.license as License)),
    notes: mode.rules[0]?.notes || '',
    loading: false,
    error: null,
    conditions: {
      depth: {
        enabled: !!mode.rules[0]?.depthCondition,
        value: mode.rules[0]?.depthCondition || 'DIRECT',
        option: mode.rules[0]?.depthCondition
          ? {
              label: mode.rules[0]?.depthCondition === 'DIRECT' ? 'In your code' : 'In a dependency',
              value: mode.rules[0]?.depthCondition,
            }
          : undefined,
      },
      name: {
        enabled: !!mode.rules[0]?.nameCondition,
        value: mode.rules[0]?.nameCondition || '',
      },
      linking: {
        enabled: !!mode.rules[0]?.linkingCondition,
        value: mode.rules[0]?.linkingCondition || 'STATIC',
        option: mode.rules[0]?.linkingCondition
          ? {
              label: mode.rules[0]?.linkingCondition === 'STATIC' ? 'Statically' : 'Dynamically',
              value: mode.rules[0]?.linkingCondition,
            }
          : undefined,
      },
    },
  };
}

export const RuleModal: VoidFunctionComponent<RuleModalProps> = (props) => {
  const initialState = makeInitialState(props.mode);

  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState<string | null>(initialState.error);
  const [type, setType] = useState<LicensingRuleType>(initialState.type);
  const [licenses, setLicenses] = useState<License[] | undefined>(initialState.licenses);
  const [notes, setNotes] = useState<string>(initialState.notes);
  const [conditions, setConditions] = useState<RuleModalState['conditions']>(initialState.conditions);

  const { mutateAsync: fetchLicenses } = useListLicenses();

  // Load fixture
  const loadLicenses = async (licenseIds: string[]) => {
    setLoading(true);
    setError(null);
    if (_.isEmpty(licenseIds)) {
      setLicenses([]);
      setLoading(false);
      return;
    }
    try {
      if (licenseIds.length === 1) {
        // When loading just one license, we need the license summary text which is not returned by the bulk selection
        // license API.
        const data = await getLicense(licenseIds[0] as string);
        setLicenses([data as License]);
      } else {
        const response = await fetchLicenses({ licenses: licenseIds, pageSize: 4999 });
        setLicenses(response.data);
      }
    } catch (err) {
      const e = err as Error;
      setError(`Error: ${e.message || e.toString()}`);
    } finally {
      setLoading(false);
    }
  };

  useOnMount(() => {
    if (props.mode && props.mode.rules && props.mode.rules.map((r) => r.licenseId)) {
      loadLicenses(props.mode.rules.map((r) => r.licenseId));
    }
  });

  useEffect(() => {
    if (props.show && props.mode && props.mode.rules && props.mode.rules.map((r) => r.licenseId)) {
      loadLicenses(props.mode.rules.map((r) => r.licenseId));
    }
  }, [props.mode, props.show]);

  const selectLicense = (licenseId: string | undefined) => {
    if (licenseId) {
      loadLicenses([licenseId]);
    }
  };

  // `eventKey` is from the props passed to `MenuItem`s, which are equivalent to `RuleAction`s.
  const setAction = (eventKey: unknown) => setType(eventKey as LicensingRuleType);

  // -- Helpers for conditions.
  const toggleCondition = (condition: keyof RuleModalState['conditions'], event: FormEvent<HTMLInputElement>) => {
    setConditions({
      ...conditions,
      [condition]: {
        ...conditions[condition],
        enabled: event.currentTarget.checked,
      },
    });
  };

  const toggleDepth = (event: FormEvent<HTMLInputElement>) => toggleCondition('depth', event);

  const selectDepth = (option: SelectOption<RuleDepthCondition>) => {
    setConditions({
      ...conditions,
      depth: {
        option,
        enabled: true,
        value: option.value,
      },
    });
  };

  const toggleName = (event: FormEvent<HTMLInputElement>) => toggleCondition('name', event);

  const setName = (value: string) => {
    setConditions({
      ...conditions,
      name: {
        enabled: true,
        value,
      },
    });
  };

  const toggleLinking = (event: FormEvent<HTMLInputElement>) => toggleCondition('linking', event);

  const selectLinking = (option: SelectOption<RuleLinkingCondition>) => {
    setConditions({
      ...conditions,
      linking: {
        option,
        enabled: true,
        value: option.value,
      },
    });
  };

  // -- Helpers for form submission.
  const submit = () => {
    if (licenses) {
      const licenseRules = _.zip(licenses, props.mode.rules);
      return props.onSubmit(
        licenseRules.map(([license, rule]) => ({
          rule: {
            licenseId: license?.licenseId as string,
            type,
            notes,
            nameCondition: conditions.name.enabled ? conditions.name.value : null,
            depthCondition: conditions.depth.enabled ? conditions.depth.value : null,
            linkingCondition: conditions.linking.enabled ? conditions.linking.value : null,
            editedRuleId: props.mode.editType === 'EDIT' ? rule?.ruleId : undefined,
          } as NewRule,
          editedRuleId: props.mode.editType === 'EDIT' ? rule?.ruleId : undefined,
        })),
      );
    }
    return undefined;
  };

  const clear = () => {
    const clearState = makeInitialState(props.mode);
    setLoading(clearState.loading);
    setError(clearState.error);
    setType(clearState.type);
    setLicenses(clearState.licenses);
    setNotes(clearState.notes);
    setConditions(clearState.conditions);
  };

  const getModalBody = () => {
    const headerText =
      props.mode.editType === 'CREATE' ? 'Create a new rule for a license' : 'Modify a rule for a license';

    const licenseSelectSection =
      licenses && licenses.length <= 1 ? (
        <>
          <div className={styles.modalSubtitle}>{headerText}</div>
          <div className={styles.section}>
            <div className={styles.checkboxLabel}>Choose a license</div>
            <LicenseOptions
              onChange={selectLicense}
              selectedLicenseId={licenses[0]?.licenseId}
            />
            {
              licenses.length === 1 && (
                <LicenseDetail
                  license={licenses[0] as License}
                  isEdit={props.mode.editType === 'EDIT'}
                />
              )
            }
          </div>
        </>
      ) : (
        <span>
          Changes made here will apply to all{' '}
          <span className={styles.ruleCount}>{licenses ? `${licenses.length} ` : ''}</span>selected licenses. If you
          need to apply different rules to different licenses, you&apos;ll need to create separate rules.
        </span>
      );

    const errorSection = error ? <Alert bsStyle="error">{error}</Alert> : null;

    const loadingSection = loading ? <Loader /> : null;

    const actionSection = (
      <div className={styles.section}>
        <div className={styles.checkboxLabel}>Action</div>
        <DropdownButton
          id="modalActionDropdownButton"
          componentClass={InputGroup.Button}
          title={licensingRuleTypeToActionRevamp(type)}
          onSelect={setAction}
        >
          <MenuItem eventKey="denied_license">Deny {plural(licenses?.length || 1, 'License')}</MenuItem>
          <MenuItem eventKey="flagged_license">Flag {plural(licenses?.length || 1, 'License')}</MenuItem>
          <MenuItem eventKey="approved_license">Approve {plural(licenses?.length || 1, 'License')}</MenuItem>
        </DropdownButton>
      </div>
    );

    const conditionsSection = (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>Conditions</div>
        <div className={styles.sectionDescription}>
          This rule will apply only if all of its conditions are satisfied.
        </div>
        <LabeledCheckbox
          label="License is found:"
          isChecked={conditions.depth.enabled}
          onChange={toggleDepth}
          isRippleDisabled
        />
        <div className={styles.checkboxRow}>
          <Select
            id="depth-select"
            smooth="left"
            options={depthSelectOptions}
            onChange={(e) => {
              if (e) {
                selectDepth(e);
              }
            }}
            value={conditions.depth.option}
          />
          <span className={styles.helperText}>
            Some licenses (e.g. MPL) only have effects if you alter the code. If you don&apos;t plan to edit
            dependencies, you can treat them differently.
          </span>
        </div>

        <LabeledCheckbox
          label="Dependency name contains:"
          isChecked={conditions.name.enabled}
          onChange={toggleName}
        />
        <div className={styles.checkboxRow}>
          <TextInput
            label="Dependency name"
            placeholder="e.g. MIT License"
            value={conditions.name.value}
            onChange={(value) => setName(value)}
            size="small"
          />
          <span className={styles.helperText}>
            If your projects follow a naming convention, you can treat them differently
            (e.g. if you own the copyright).
          </span>
        </div>

        <LabeledCheckbox
          label="Dependency is linked:"
          isChecked={conditions.linking.enabled}
          onChange={toggleLinking}
        />
        <div className={styles.checkboxRow}>
          <Select
            id="link-select"
            smooth="left"
            options={linkSelectOptions}
            onChange={(e) => {
              if (e) {
                selectLinking(e);
              }
            }}
            value={conditions.linking.option}
          />
          <span className={styles.helperText}>
            Depending on how you distribute your software, some licenses (e.g. LGPL) may only be relevant if you
            statically link your code.
          </span>
        </div>

      </div>
    );

    const notesSection = (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>Notes</div>
        <div className={styles.helperText}>Add any information you want to keep.</div>
        <TextArea
          label="Notes"
          placeholder="e.g. We deny GPL since, for our use case, it would force us to disclose our source code."
          value={notes || ''}
          onChange={(e) => setNotes(e)}
        />
      </div>
    );

    if (loading) {
      return (
        <div>
          {licenseSelectSection}
          {errorSection}
          {loadingSection}
        </div>
      );
    }

    return (
      <div>
        {licenseSelectSection}
        {errorSection}
        {licenses && licenses.length > 0 && (
          <div>
            {actionSection}
            {conditionsSection}
            {notesSection}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal show={props.show} onHide={props.onClose} onShow={clear}>
      <Modal.Header closeButton>
        <Modal.Title>{props.mode.editType === 'CREATE' ? 'New Rule' : 'Edit Rule'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{getModalBody()}</Modal.Body>
      <Modal.Footer>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button onClick={submit} type="submit" bsStyle="primary">
          {props.mode.editType === 'CREATE'
            ? `Add ${plural(licenses?.length || 1, 'Rule')}`
            : `Edit ${plural(licenses?.length || 1, 'Rule')}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
